import React, { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, KeyboardEvent, SetStateAction, UIEvent } from "react";

import assistantService, {
  type ChatMessage as AssistantChatMessage,
} from "../services/AssistantService";

/**
 * Extends the base ChatMessage interface to include additional properties
 * needed for the chat functionality
 */
export interface ExtendedChatMessage extends AssistantChatMessage {
  /** Optional ID for tracking placeholders and matching responses to requests */
  id?: string;
}

/**
 * Return type for the useAssistantChat hook
 */
export interface AssistantChatHookReturn {
  /** Array of chat messages in the conversation */
  messages: ExtendedChatMessage[];

  /** Current value of the input text field */
  inputText: string;

  /** Function to update the input text */
  setInputText: Dispatch<SetStateAction<string>>;

  /** Whether a message is currently being sent/processed */
  isLoading: boolean;

  /** Error message if any error occurred, null otherwise */
  error: string | null;

  /** Ref to scroll to the end of messages */
  messagesEndRef: React.RefObject<HTMLDivElement>;

  /** Ref for the messages container for scroll handling */
  messagesContainerRef: React.RefObject<HTMLDivElement>;

  /** Ref for the input field to enable auto-focus */
  inputRef: React.RefObject<HTMLInputElement>;

  /** Function to create a new chat session */
  createNewSession: (componentId?: string) => void;

  /** Function to send a new message */
  handleSendMessage: (componentId?: string) => Promise<void>;

  /** Handler for keyboard events (e.g., Enter to send) */
  handleKeyPress: (e: KeyboardEvent, componentId?: string) => void;

  retryLastRequest: () => void;

  /** Create component-specific scroll behavior and refs */
  useComponentRefs: (componentId: string) => {
    messagesEndRef: React.RefObject<HTMLDivElement>;
    messagesContainerRef: React.RefObject<HTMLDivElement>;
    inputRef: React.RefObject<HTMLInputElement>;
    shouldAutoScroll: boolean;
    handleScroll: (e: UIEvent<HTMLDivElement>) => void;
  };
}

/**
 * Focuses the input element for a specific component
 */
const focusComponentInput = (
  componentId: string | undefined,
  componentRefs: React.MutableRefObject<Record<string, any>>,
) => {
  // requestAnimationFrame ensures DOM updates are flushed so that the input focus works reliably
  requestAnimationFrame(() => {
    if (componentId && componentRefs.current[componentId]) {
      const inputRef = componentRefs.current[componentId].inputRef;
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  });
};

/**
 * A custom hook that provides all the functionality needed for an assistant chat interface.
 *
 * This hook handles:
 * - Message state management
 * - Sending and receiving messages
 * - Auto-scrolling behavior
 * - Error handling and retries
 * - Session management
 *
 * @param options Configuration options for the hook
 * @returns An object containing all the state and functions needed for a chat interface
 *
 */
export const useAssistantChat = (): AssistantChatHookReturn => {
  const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [componentAutoScrollStates, setComponentAutoScrollStates] = useState<
    Record<string, boolean>
  >({});
  const componentRefs = useRef<
    Record<
      string,
      {
        messagesEndRef: React.RefObject<HTMLDivElement>;
        messagesContainerRef: React.RefObject<HTMLDivElement>;
        inputRef: React.RefObject<HTMLInputElement>;
      }
    >
  >({});

  const createPlaceholderMessage = (text: string = ""): ExtendedChatMessage => {
    return {
      text,
      isUser: false,
      timestamp: new Date(),
      id: Date.now().toString(),
    };
  };

  const updateMessageById = (id: string, newText: string) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, text: newText } : msg)),
    );
  };

  /**
   * Fetches the initial greeting message from the assistant service
   * This is called automatically when the hook is initialized
   * @param componentId Optional component ID to focus the input after greeting is received
   */
  const getInitialGreeting = useCallback(async (componentId?: string) => {
    try {
      setIsLoading(true);
      const placeholderMessage = createPlaceholderMessage();
      setMessages([placeholderMessage]);

      await assistantService.sendMessage("", (chunk) => {
        updateMessageById(placeholderMessage.id!, chunk);
      });
    } catch (err: any) {
      console.error("Error getting initial greeting:", err);
      setMessages([
        {
          text: "Hello! How can I help you today?",
          isUser: false,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
      focusComponentInput(componentId, componentRefs);
    }
  }, []);

  const useComponentRefs = useCallback(
    (componentId: string) => {
      if (!componentRefs.current[componentId]) {
        componentRefs.current[componentId] = {
          messagesEndRef: React.createRef<HTMLDivElement>(),
          messagesContainerRef: React.createRef<HTMLDivElement>(),
          inputRef: React.createRef<HTMLInputElement>(),
        };
      }

      const componentShouldAutoScroll =
        componentAutoScrollStates[componentId] ?? true;

      const componentHandleScroll = (e: UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        // If user scrolls up, disable auto-scroll
        // If user scrolls to bottom, re-enable auto-scroll
        const isAtBottom =
          Math.abs(scrollHeight - scrollTop - clientHeight) < 1;

        setComponentAutoScrollStates((prev) => ({
          ...prev,
          [componentId]: isAtBottom,
        }));
      };

      return {
        messagesEndRef: componentRefs.current[componentId].messagesEndRef,
        messagesContainerRef:
          componentRefs.current[componentId].messagesContainerRef,
        inputRef: componentRefs.current[componentId].inputRef,
        shouldAutoScroll: componentShouldAutoScroll,
        handleScroll: componentHandleScroll,
      };
    },
    [componentAutoScrollStates],
  );

  useEffect(() => {
    Object.keys(componentRefs.current).forEach((componentId) => {
      // Default to auto-scroll enabled until the user scrolls away from the bottom
      const shouldScroll = componentAutoScrollStates[componentId] ?? true;
      if (
        shouldScroll &&
        componentRefs.current[componentId]?.messagesEndRef.current
      ) {
        componentRefs.current[
          componentId
        ]?.messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    });
  }, [messages, componentAutoScrollStates]);

  useEffect(() => {
    if (messages.length > 0) {
      // Reset component auto-scroll states when new messages arrive
      setComponentAutoScrollStates((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          updated[key] = true;
        });
        return updated;
      });
    }
  }, [messages.length]);

  const createNewSession = (componentId?: string) => {
    setMessages([]);
    assistantService.createNewSession();
    getInitialGreeting(componentId);
  };

  const sendWithRetry = async (
    userMessageText: string,
    placeholderId: string,
  ): Promise<void> => {
    let responseText = "";
    let retryCount = 0;
    const maxRetries = 2;

    const attemptSend = async (): Promise<void> => {
      try {
        await assistantService.sendMessage(userMessageText, (chunk) => {
          responseText += chunk;
          updateMessageById(placeholderId, responseText);
        });
      } catch (error: any) {
        const errorMessage = error?.message || "Unknown error";

        if (
          retryCount < maxRetries &&
          (errorMessage.includes("timeout") ||
            errorMessage.includes("Gateway timeout"))
        ) {
          retryCount++;
          updateMessageById(
            placeholderId,
            `Connection timed out. Retrying (${retryCount}/${maxRetries})...`,
          );

          await new Promise((resolve) => setTimeout(resolve, 2000));
          return attemptSend();
        }

        throw error;
      }
    };

    await attemptSend();

    if (!responseText) {
      updateMessageById(
        placeholderId,
        "Sorry, I couldn't generate a response.",
      );
    }
  };

  const handleMessageError = (err: any, placeholderId: string) => {
    console.error("Error sending message:", err);
    const errorMessage =
      err?.message || "Failed to get a response. Please try again.";
    setError(errorMessage);

    // Format error message based on error type
    let formattedMessage =
      "Sorry, I encountered an error processing your request. ";
    if (errorMessage.includes("Authentication")) {
      formattedMessage += "Please log in again or refresh your session.";
    } else if (errorMessage.includes("timeout")) {
      formattedMessage += "The service might be temporarily unavailable.";
    } else {
      formattedMessage += "Please try again later.";
    }

    updateMessageById(placeholderId, formattedMessage);
  };

  /**
   * Handles sending a new message to the assistant
   * Creates a user message and a placeholder for the assistant's response
   */
  const handleSendMessage = async (componentId?: string) => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = {
      text: inputText,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    const placeholderMessage = createPlaceholderMessage();
    setMessages((prev) => [...prev, placeholderMessage]);

    setInputText("");
    setIsLoading(true);
    setError(null);

    try {
      await sendWithRetry(userMessage.text, placeholderMessage.id!);
    } catch (err: any) {
      handleMessageError(err, placeholderMessage.id!);
    } finally {
      setIsLoading(false);
      focusComponentInput(componentId, componentRefs);
    }
  };

  /**
   * Handles keyboard events in the input field
   * Sends the message when Enter is pressed (without Shift)
   */
  const handleKeyPress = (e: KeyboardEvent, componentId?: string) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(componentId);
    }
  };

  /**
   * Retries sending the last user message
   */
  const retryLastRequest = () => {
    if (messages.length < 2) return;

    const lastUserMessageIndex = [...messages]
      .reverse()
      .findIndex((msg) => msg.isUser);
    if (lastUserMessageIndex === -1) return;

    const lastUserMessage =
      messages[messages.length - 1 - lastUserMessageIndex];

    const userMessagePosition = messages.length - 1 - lastUserMessageIndex;
    const messagesUpToUser = messages.slice(0, userMessagePosition + 1);
    setMessages(messagesUpToUser);

    const placeholderMessage = createPlaceholderMessage();
    setMessages((prev) => [...prev, placeholderMessage]);

    setIsLoading(true);
    setError(null);

    sendWithRetry(lastUserMessage.text, placeholderMessage.id!)
      .catch((err: any) => {
        handleMessageError(err, placeholderMessage.id!);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return {
    messages,
    inputText,
    setInputText,
    isLoading,
    error,
    messagesEndRef,
    messagesContainerRef,
    inputRef,
    createNewSession,
    handleSendMessage,
    handleKeyPress,
    retryLastRequest,
    useComponentRefs,
  };
};
